import type { CrudAdapter } from '@botto/core';
import { useCrudDataSource } from '@botto/core';
import { cdpClient } from '../client';
import type { Project, ProjectCreate, ProjectUpdate } from '../types';

let cachedProjects: Project[] | null = null;

async function loadAllProjects(): Promise<Project[]> {
  const { data, error } = await cdpClient.GET('/projects');
  if (error) throw error;
  cachedProjects = data?.items ?? [];
  return cachedProjects;
}

export function invalidateProjectsCache(): void {
  cachedProjects = null;
}

export const projectsAdapter: CrudAdapter<Project, ProjectCreate, ProjectUpdate> = {
  async list({ offset, limit }) {
    const all = cachedProjects ?? (await loadAllProjects());
    const items = all.slice(offset, offset + limit);
    return { items, total: all.length };
  },
  async byKey(id) {
    const { data, error } = await cdpClient.GET('/projects/{project_id}', {
      params: { path: { project_id: String(id) } },
    });
    if (error) throw error;
    return data as Project;
  },
  async create(payload) {
    const { data, error } = await cdpClient.POST('/projects', { body: payload });
    if (error) throw error;
    invalidateProjectsCache();
    return { id: data?.id ?? '' };
  },
  async update(payload) {
    const row = payload as ProjectUpdate & { id: string };
    const id = row.id;
    const { name, status, system_id, sender_name } = row;
    const { data, error } = await cdpClient.PATCH('/projects/{project_id}', {
      params: { path: { project_id: String(id) } },
      body: { name, status, system_id, sender_name },
    });
    if (error) throw error;
    invalidateProjectsCache();
    return (data ?? payload) as Project;
  },
  async remove(id) {
    const { error } = await cdpClient.DELETE('/projects/{project_id}', {
      params: { path: { project_id: String(id) } },
    });
    if (error) throw error;
    invalidateProjectsCache();
  },
};

export const projectsStore = useCrudDataSource(projectsAdapter, { keyExpr: 'id' });

export async function createProject(payload: ProjectCreate): Promise<Project> {
  const created = await projectsAdapter.create!(payload);
  const id = String(created.id ?? '');
  if (!id) throw new Error('Пустой ответ API');
  const row = await projectsAdapter.byKey?.(id);
  if (!row) throw new Error('Проект создан, но не удалось прочитать');
  return row;
}

export async function fetchActiveProjects(): Promise<Project[]> {
  const { data, error } = await cdpClient.GET('/projects', {
    params: { query: { status: 'active' } },
  });
  if (error) throw error;
  return data?.items ?? [];
}
